// src/config/voucherLayoutConfig.js
//
// FRONTEND VOUCHER LAYOUT CONFIG
// ==============================
//
// This file configures the HTML/CSS VOUCHER preview on the frontend.
//
// It is intentionally a VOUCHER-ONLY config.
//
// Used by <VoucherPreview /> on voucher-related frontend screens such as:
//   - ShopPage (public shop preview before payment)
//   - VoucherOverviewPage (backoffice preview / print modal)
//   - other voucher-only admin/shop flows
//
// It is a LIGHTWEIGHT frontend mirror of backend PDF concepts such as:
//   - backend/pdf/voucherThemeConfig.js
//   - backend/pdf/generateVoucherPdfBuffer.js
//   - backend/pdf/templates/voucher/*
//
// IMPORTANT SCOPE AFTER THE PDF FAMILY SPLIT
// ------------------------------------------
// The backend PDF layer is now moving toward physically separated template
// families:
//
//   - templates/voucher/*
//   - templates/package/*
//
// However, THIS FILE remains intentionally voucher-only.
//
// Why?
// ----
// Because on the frontend, not every screen is package-aware:
//
//   - ShopPage currently sells vouchers only
//   - ShopPage currently previews vouchers only
//   - ShopPage currently works with voucher-only templateVersion/theme logic
//
// Therefore this file SHOULD NOT try to become a mixed voucher+package config.
// That would make the contract less clear and would couple voucher-only UI
// to package semantics unnecessarily.
//
// If later you build a dedicated package preview on the frontend,
// the recommended architecture is:
//
//   src/config/voucherLayoutConfig.js   -> voucher-only
//   src/config/packageLayoutConfig.js   -> package-only
//
// instead of forcing both document families into one mixed config object.
//
// GOALS
// -----
//   1) Keep one clear frontend source of truth for voucher theme keys.
//   2) Provide labels & colors for voucher themes used by HTML preview.
//   3) Keep global texts like salonName / validityText reasonably aligned
//      with the backend PDF flow, so that HTML preview and generated PDF
//      feel like the same product family.
//   4) Keep voucher templateVersion semantics aligned with backend
//      VOUCHER-side PDF logic.
//   5) Preserve legacy compatibility where useful ("warm", "v1") without
//      polluting new canonical UI logic.
//
// IMPORTANT
// ---------
//   - Theme keys here MUST match backend PDF theme keys for vouchers, e.g.:
//        "default", "christmas", "valentine"
//
//   - The frontend may still receive some legacy values (e.g. "warm").
//     We normalize them via `normalizeTheme()`.
//
//   - Legacy voucher template versions (e.g. "v1") are normalized via
//     `normalizeVoucherTemplateVersion()`.
//
//   - This file does NOT know anything about Sequelize, database tables,
//     or backend rendering internals. It only describes voucher preview
//     semantics for the frontend.
//
//   - This file does NOT define package template versions or package themes.
//     That separation is intentional and desirable.
//
// ---------------------------------------------------------------------------
// EXPLICIT FRONTEND BUSINESS SCOPE
// ---------------------------------------------------------------------------

/**
 * Explicit frontend business mode handled by this config.
 *
 * We expose this mostly as a semantic hint for developers and future code,
 * not because the whole app currently needs to branch on it everywhere.
 *
 * The key point is:
 *   this config belongs to the VOUCHER family only.
 */
export const FRONTEND_VOUCHER_BUSINESS_MODE = "voucher";

// ---------------------------------------------------------------------------
// THEME REGISTRY (FRONTEND VOUCHER VIEW)
// ---------------------------------------------------------------------------
//
// Canonical voucher theme keys used across the voucher-related UI:
//
//   - preview.theme on the frontend (ShopPage + admin voucher preview)
//   - orders.theme in the database for voucher sales
//   - vouchers.theme in the database
//   - backend/pdf/voucherThemeConfig.js (backend PDF theme registry)
//
// If you add a new VOUCHER theme (for example: "birthday"), you should:
//   1) Add it here (with label + colors),
//   2) Add matching CSS classes in VoucherPreview.css,
//   3) Add a matching entry in backend voucherThemeConfig,
//   4) Expose it in voucher-only UI where appropriate,
//   5) Decide whether it also needs a voucher templateVersion mapping.
//
// IMPORTANT:
// ----------
// We keep a legacy "warm" alias in the registry for compatibility,
// but new UI should operate on canonical keys only.
//
export const VOUCHER_THEMES = {
  default: {
    key: "default",
    label: "Klasyczny", // used on ShopPage buttons / admin UI
  },

  christmas: {
    key: "christmas",
    label: "Świąteczny",
  },

  valentine: {
    key: "valentine",
    label: "Walentynkowy",
  },

  // NOTE:
  //  - "warm" is a legacy front-end key used before the app standardized on
  //    "valentine".
  //  - We keep it as an internal alias for backwards compatibility
  //    (old data, old URLs, stale local state, older orders, etc.).
  //  - `normalizeTheme("warm")` returns "valentine".
  //
  // IMPORTANT:
  //  - This entry should generally NOT be offered as a new selectable option
  //    in modern UI.
  warm_legacy: {
    key: "warm",
    label: "Ciepły (legacy)",
  },
};

/**
 * Canonical voucher theme keys list (only real/canonical themes,
 * not the legacy alias).
 *
 * This is the list new UI should use when it needs only modern supported keys.
 */
export const SUPPORTED_THEME_KEYS = ["default", "christmas", "valentine"];

/**
 * Canonical voucher theme option objects.
 *
 * This is convenient for selectors / pills / toggles where you want:
 *   - stable order
 *   - only canonical options
 *   - no accidental display of legacy aliases
 */
export const VOUCHER_THEME_OPTIONS = SUPPORTED_THEME_KEYS.map(
  (key) => VOUCHER_THEMES[key]
);

// ---------------------------------------------------------------------------
// TEMPLATE VERSION REGISTRY (VOUCHER-ONLY FRONTEND VIEW)
// ---------------------------------------------------------------------------
//
// IMPORTANT:
// ----------
// These are VOUCHER template versions only.
//
// After the backend PDF split, package template versions belong to the
// package family and should NOT be added here.
//
// Current canonical voucher template versions:
//
//   - "company"
//   - "christmas"
//   - "valentine"
//
// Legacy compatibility:
//   - "v1" should be treated as "company"
//
// This keeps frontend voucher UI aligned with backend voucher PDF logic.
//
export const DEFAULT_VOUCHER_TEMPLATE_VERSION = "company";

/**
 * Canonical supported voucher templateVersion keys.
 *
 * These should match the voucher-side semantics used by backend PDF logic.
 */
export const SUPPORTED_VOUCHER_TEMPLATE_VERSIONS = [
  "company",
  "christmas",
  "valentine",
];

/**
 * Voucher template version options for UI components.
 *
 * This can be used in selectors, pills, radio groups, etc.
 */
export const VOUCHER_TEMPLATE_VERSION_OPTIONS = [
  { value: "company", label: "Klasyczny" },
  { value: "christmas", label: "Świąteczny" },
  { value: "valentine", label: "Walentynkowy" },
];

/**
 * Legacy voucher template version aliases.
 *
 * We keep these as explicit documentation of compatibility behavior.
 * Right now:
 *   - "v1" -> "company"
 */
export const LEGACY_VOUCHER_TEMPLATE_VERSION_ALIASES = {
  v1: "company",
};

// ---------------------------------------------------------------------------
// TEMPLATE VERSION → THEME MAPPING (FRONTEND VOUCHER VIEW)
// ---------------------------------------------------------------------------
//
// Conceptual model:
//   - templateVersion -> tells us which graphic motif/version was chosen
//                        in voucher UI
//   - theme           -> the actual theme key used by preview + backend PDF
//
// Current canonical mapping:
//
//   - "company"   -> theme "default"
//   - "christmas" -> theme "christmas"
//   - "valentine" -> theme "valentine"
//
// This mapping is intentionally simple and 1:1 with backend VOUCHER logic.
//
// IMPORTANT AFTER FAMILY SPLIT
// ----------------------------
// This is intentionally voucher-only.
// Package templateVersion->theme mapping belongs to package frontend config,
// not here.
//
export const templateVersionToThemeMap = {
  company: "default",
  christmas: "christmas",
  valentine: "valentine",
};

// ---------------------------------------------------------------------------
// CORE LAYOUT CONFIG
// ---------------------------------------------------------------------------
//
// NOTE:
//   - Only some fields are directly used by <VoucherPreview /> today.
//   - Others exist to keep this file close to the backend concepts and to make
//     future refactors easier.
//   - This object remains voucher-only by design.
//
export const voucherLayoutConfig = {
  // Explicit semantic hint:
  // this config belongs to the voucher family only.
  businessMode: FRONTEND_VOUCHER_BUSINESS_MODE,

  // Displayed salon name (header of the preview) when caller does not override
  // `preview.salonName`. This should match what you show in the PDF header.
  salonName: "HB Health & Beauty",

  // Optional location line (currently not used directly by VoucherPreview,
  // but kept to mirror backend config and for potential future use).
  locationLine: "Konin",

  // Label under the salon name in the header.
  // Kept in Polish because the voucher itself is Polish-language.
  voucherLabel: "Bon podarunkowy",

  // Default validity text used by VoucherPreview when the caller does not
  // provide an explicit `expiresAtLabel` (e.g. in shop live preview).
  //
  // IMPORTANT ALIGNMENT NOTE
  // ------------------------
  // This default should stay aligned with backend fallback behavior as closely
  // as practical.
  //
  // At the moment backend/pdf/generateVoucherPdfBuffer.js falls back to:
  //   "Bon ważny przez 12 miesięcy od daty zakupu."
  //
  // Therefore we use the same default here.
  //
  // Backend PDFs:
  //   - If voucher.expiration_date is set, they may display a concrete date:
  //       "Bon ważny do 10.02.2026"
  //   - Otherwise, they fall back to the generic rule above.
  //
  // Frontend preview:
  //   - We use a generic rule here, so ShopPage and voucher backoffice preview
  //     always show something meaningful even before a concrete expiry date
  //     exists.
  validityText: "Bon ważny przez 12 miesięcy od daty zakupu.",

  // Optional footer note – not currently read by <VoucherPreview /> directly
  // in every place (the component may render its own disclaimer), but kept
  // for symmetry with backend config and potential future use.
  footerNote:
    "Szczegółowe zasady wykorzystania bonu znajdziesz w Regulaminie salonu.",

  // Theme registry and defaults – used by UI for theme selectors, etc.
  themes: VOUCHER_THEMES,
  supportedThemeKeys: SUPPORTED_THEME_KEYS,
  themeOptions: VOUCHER_THEME_OPTIONS,
  defaultThemeKey: "default",

  // Voucher templateVersion registry and defaults.
  supportedTemplateVersions: SUPPORTED_VOUCHER_TEMPLATE_VERSIONS,
  templateVersionOptions: VOUCHER_TEMPLATE_VERSION_OPTIONS,
  defaultTemplateVersion: DEFAULT_VOUCHER_TEMPLATE_VERSION,

  // Also expose template-version mapping on the config object itself
  // (optional convenience; not required by React code, but nice to have
  // if someone imports only voucherLayoutConfig).
  templateVersionToThemeMap,

  // -------------------------------------------------------------------------
  // THEME COLOR TOKENS (HTML PREVIEW ONLY)
  // -------------------------------------------------------------------------
  //
  // VoucherPreview.css uses CSS classes (vp-root--<themeKey>) to style the
  // preview. These colors are a JS mirror of those choices and can be used
  // by UI around the preview (e.g. theme badges, gradients, selectors).
  //
  // They do NOT directly control backend PDF colors.
  // Backend PDF uses its own token registry in voucherThemeConfig.js.
  //
  colors: {
    // Canonical "default" theme – neutral / salon branding.
    default: {
      background: "#ffffff",
      accent: "#111111",
      text: "#111111",
      subtleText: "#555555",
      border: "#e5e5e5",
      qrBackground: "#f5f5f5",
    },

    // Canonical "valentine" theme – warm, slightly pink/warm tone.
    // This is the visual successor to the old "warm" theme.
    valentine: {
      background: "#fff8f0",
      accent: "#c77b4a",
      text: "#3b2e25",
      subtleText: "#7b5c47",
      border: "#f0d2b8",
      qrBackground: "#fff1e0",
    },

    // Legacy "warm" entry kept to avoid breaking any code that might still
    // read colors.warm directly. It simply reuses the "valentine" palette.
    warm: {
      background: "#fff8f0",
      accent: "#c77b4a",
      text: "#3b2e25",
      subtleText: "#7b5c47",
      border: "#f0d2b8",
      qrBackground: "#fff1e0",
    },

    // "Christmas" theme – more green-ish, festive tone.
    christmas: {
      background: "#ffffff",
      accent: "#0b6623",
      text: "#111111",
      subtleText: "#555555",
      border: "#e0e0e0",
      qrBackground: "#f6fff6",
    },
  },

  // Spacing tokens – currently not read directly in JS,
  // but can serve as a shared reference if you ever compute
  // sizes or paddings programmatically in the preview.
  spacing: {
    pagePadding: 40,
    sectionGap: 16,
    headerGap: 24,
  },
};

// ---------------------------------------------------------------------------
// normalizeTheme(theme)
// ---------------------------------------------------------------------------
//
// Normalizes any input string into one of the canonical voucher theme keys:
//
//   - "default"
//   - "christmas"
//   - "valentine"
//
// Backwards compatibility:
//
//   - "warm" (old frontend key) -> "valentine"
//   - anything unknown / empty  -> "default"
//
// This function is used by:
//   - VoucherPreview.jsx -> to select CSS classes (vp-root--<themeKey>)
//   - any UI mapping logic that wants a stable key for voucher orders/vouchers
//   - backoffice preview mapping when reading voucher.theme from DB
//
// IMPORTANT:
//   - We purposely do NOT return "warm" as a normalized key.
//   - It is treated as an alias of "valentine" so your data can gradually
//     migrate without breaking previews.
//   - This is a frontend voucher-only normalization rule.
//
export function normalizeTheme(theme) {
  if (!theme || typeof theme !== "string") {
    return voucherLayoutConfig.defaultThemeKey;
  }

  const t = theme.trim().toLowerCase();

  // Legacy alias -> canonical valentine theme.
  if (t === "warm") {
    return "valentine";
  }

  if (SUPPORTED_THEME_KEYS.includes(t)) {
    return t;
  }

  return voucherLayoutConfig.defaultThemeKey;
}

/**
 * normalizeVoucherTemplateVersion(version)
 *
 * Normalizes any incoming voucher templateVersion into a canonical
 * voucher templateVersion key.
 *
 * Canonical outputs:
 *   - "company"
 *   - "christmas"
 *   - "valentine"
 *
 * Backwards compatibility:
 *   - "v1" -> "company"
 *
 * Fallback:
 *   - unknown / empty -> "company"
 *
 * IMPORTANT:
 * ----------
 * This helper is intentionally voucher-only.
 * Package templateVersion normalization should live in package frontend config,
 * not here.
 */
export function normalizeVoucherTemplateVersion(version) {
  if (!version || typeof version !== "string") {
    return voucherLayoutConfig.defaultTemplateVersion;
  }

  const v = version.trim().toLowerCase();

  if (LEGACY_VOUCHER_TEMPLATE_VERSION_ALIASES[v]) {
    return LEGACY_VOUCHER_TEMPLATE_VERSION_ALIASES[v];
  }

  if (SUPPORTED_VOUCHER_TEMPLATE_VERSIONS.includes(v)) {
    return v;
  }

  return voucherLayoutConfig.defaultTemplateVersion;
}

/**
 * getThemePreset(themeKey)
 *
 * Convenience helper for UI code:
 *   - Accepts any string (may be canonical or legacy),
 *   - Normalizes it to a canonical key,
 *   - Returns a corresponding theme metadata object.
 *
 * Example:
 *   const themePreset = getThemePreset(order.theme);
 *   console.log(themePreset.label); // "Świąteczny", etc.
 */
export function getThemePreset(themeKey) {
  const normalizedKey = normalizeTheme(themeKey);

  if (normalizedKey === "valentine") {
    // Canonical valentine theme; this covers legacy "warm" as well.
    return VOUCHER_THEMES.valentine;
  }

  // For other canonical themes we expect a direct entry.
  return VOUCHER_THEMES[normalizedKey] || VOUCHER_THEMES.default;
}

/**
 * getThemeColors(themeKey)
 *
 * Returns HTML-preview color tokens for a given theme key.
 *
 * This helper is useful when some UI code wants color tokens directly rather
 * than relying only on CSS classes.
 */
export function getThemeColors(themeKey) {
  const normalizedKey = normalizeTheme(themeKey);
  return (
    voucherLayoutConfig.colors[normalizedKey] ||
    voucherLayoutConfig.colors[voucherLayoutConfig.defaultThemeKey]
  );
}

/**
 * getThemeForTemplateVersion(version)
 *
 * High-level helper that:
 *   - takes a voucher templateVersion
 *     (e.g. "company", "christmas", "valentine", legacy "v1"),
 *   - normalizes it with voucher-only rules,
 *   - maps it via templateVersionToThemeMap,
 *   - falls back to normalizeTheme() if needed.
 *
 * This is useful if other parts of the frontend (or tests) want to reuse
 * the same logic as ShopPage / backend voucher flow for deriving theme
 * from voucher template version.
 */
export function getThemeForTemplateVersion(version) {
  const normalizedVersion = normalizeVoucherTemplateVersion(version);
  const mapped = templateVersionToThemeMap[normalizedVersion];

  if (mapped) {
    return normalizeTheme(mapped);
  }

  // Defensive fallback:
  // if mapping is somehow incomplete, still try to interpret the version
  // as a raw theme key.
  return normalizeTheme(normalizedVersion);
}

/**
 * isSupportedVoucherTemplateVersion(version)
 *
 * Small helper for validation/guard code in voucher-only UI flows.
 */
export function isSupportedVoucherTemplateVersion(version) {
  const normalized = normalizeVoucherTemplateVersion(version);
  return SUPPORTED_VOUCHER_TEMPLATE_VERSIONS.includes(normalized);
}

/**
 * isSupportedVoucherTheme(theme)
 *
 * Small helper for validation/guard code in voucher-only UI flows.
 */
export function isSupportedVoucherTheme(theme) {
  const normalized = normalizeTheme(theme);
  return SUPPORTED_THEME_KEYS.includes(normalized);
}